<template>
  <Transition name="fade">
    <div
      v-if="open"
      class="fixed inset-0 z-[10002] flex items-end sm:items-center justify-center"
    >
      <div class="absolute inset-0 bg-black/40" @click="emit('close')"></div>
      <div
        class="relative w-full sm:max-w-[560px] bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] sm:max-h-[85vh] overflow-y-auto"
      >
        <!-- Mobile drag handle -->
        <div class="flex justify-center pt-2 pb-1 sm:hidden">
          <div class="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        <button
          class="z-10 absolute top-3 sm:top-4 right-3 p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#d0a444]/40"
          aria-label="Fermer"
          @click="emit('close')"
        >
          <XMarkIcon class="w-5 h-5 text-gray-500" />
        </button>

        <!-- Body -->
        <div class="px-4 sm:px-6 pb-6 grid grid-cols-1 gap-2 pt-2 sm:pt-5">
          <!-- Tabs (left-aligned, not full width, animated underline) -->
          <div v-if="activeTab !== 'reset'" class="relative">
            <div class="flex items-center gap-4 sm:gap-6">
              <button
                class="py-2 px-1 text-left font-['Outfit'] text-[13px] sm:text-[14px] leading-[18px] transition-colors font-medium"
                :class="
                  activeTab === 'signin' ? 'text-[#111827]' : 'text-gray-500'
                "
                @click="activeTab = 'signin'"
              >
                Se connecter
              </button>
              <button
                class="py-2 px-1 text-left font-['Outfit'] text-[13px] sm:text-[14px] leading-[18px] transition-colors font-medium"
                :class="
                  activeTab === 'signup' ? 'text-[#111827]' : 'text-gray-500'
                "
                @click="activeTab = 'signup'"
              >
                Créer un compte
              </button>
            </div>
            <div class="mt-1 h-px bg-gray-200"></div>
          </div>

          <!-- Reset Password Header with Back Button -->
          <div
            v-else
            class="relative flex items-center gap-2 sm:gap-3 pb-3 sm:pb-4 border-b border-gray-200"
          >
            <button
              class="p-1.5 sm:p-1 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#d0a444]/40"
              aria-label="Retour"
              @click="
                activeTab = 'signin';
                resetEmailSent = false;
                resetEmail = '';
                resetError = '';
              "
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="2"
                stroke="currentColor"
                class="w-5 h-5 text-gray-700"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
                />
              </svg>
            </button>
            <h2 class="text-base sm:text-lg font-semibold text-[#111827]">
              Réinitialiser le mot de passe
            </h2>
          </div>

          <!-- Sign In (no transition) -->
          <form
            v-if="activeTab === 'signin'"
            class="space-y-3 sm:space-y-4 mt-3 sm:mt-4"
            @submit.prevent="onSignIn"
          >
            <div>
              <UiTextInput
                v-model="signin.email"
                type="email"
                label="Email"
                :error-message="signInErrors.email"
                autocomplete="email"
              />
            </div>
            <div>
              <UiTextInput
                v-model="signin.password"
                type="password"
                label="Mot de passe"
                :error-message="signInErrors.password || signInError"
                autocomplete="current-password"
              />
            </div>
            <div class="flex justify-end">
              <button
                type="button"
                class="text-sm text-[#d0a444] hover:text-[#b8923d] transition-colors"
                @click="
                  activeTab = 'reset';
                  resetEmailSent = false;
                  resetEmail = '';
                  resetError = '';
                "
              >
                Mot de passe oublié ?
              </button>
            </div>
            <UiButton
              variant="primary"
              size="base"
              :loading="loading"
              class="w-full"
              type="submit"
              >Se connecter</UiButton
            >
          </form>

          <!-- Sign Up (no transition) -->
          <form
            v-if="activeTab === 'signup'"
            class="space-y-3 sm:space-y-4 mt-3 sm:mt-4"
            @submit.prevent="onSignUp"
          >
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <UiTextInput
                  v-model="signup.firstName"
                  type="text"
                  label="Prénom"
                  :error-message="signUpErrors.firstName"
                  autocomplete="given-name"
                />
              </div>
              <div>
                <UiTextInput
                  v-model="signup.lastName"
                  type="text"
                  label="Nom"
                  :error-message="signUpErrors.lastName"
                  autocomplete="family-name"
                />
              </div>
            </div>
            <div>
              <UiTextInput
                v-model="signup.email"
                type="email"
                label="Email"
                :error-message="signUpErrors.email"
                autocomplete="email"
              />
            </div>
            <div class="space-y-2">
              <UiTextInput
                v-model="signup.password"
                type="password"
                label="Mot de passe"
                :error-message="signUpErrors.password"
                autocomplete="new-password"
              />
              <div class="flex items-center gap-2">
                <div
                  class="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden"
                >
                  <div
                    class="h-full transition-all duration-300"
                    :class="strengthBarClass"
                    :style="{ width: strengthPercent + '%' }"
                  />
                </div>
                <span class="text-xs" :class="strengthTextClass">{{
                  strengthLabel
                }}</span>
              </div>
            </div>
            <div>
              <UiTextInput
                v-model="signup.confirmPassword"
                type="password"
                label="Confirmer le mot de passe"
                :error-message="confirmError || signUpErrors.confirmPassword"
                autocomplete="new-password"
              />
            </div>
            <UiButton
              variant="primary"
              size="base"
              :loading="loading"
              class="w-full"
              type="submit"
              :disabled="!!confirmError && !isSignUpValidating"
              >Créer mon compte</UiButton
            >
          </form>

          <!-- Password Reset Form -->
          <form
            v-if="activeTab === 'reset'"
            class="space-y-3 sm:space-y-4 mt-3 sm:mt-4"
            @submit.prevent="onResetPassword"
          >
            <div v-if="!resetEmailSent">
              <p class="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
                Entrez votre adresse email et nous vous enverrons un code pour
                réinitialiser votre mot de passe.
              </p>
              <div>
                <UiTextInput
                  v-model="resetEmail"
                  type="email"
                  label="Email"
                  :error-message="resetError"
                  autocomplete="email"
                />
              </div>
              <UiButton
                variant="primary"
                size="base"
                :loading="loading"
                class="w-full mt-4"
                type="submit"
                >Réinitialiser mon mot de passe</UiButton
              >
            </div>
            <div v-else class="text-center py-3 sm:py-4">
              <div
                class="w-14 h-14 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="2"
                  stroke="currentColor"
                  class="w-7 h-7 sm:w-8 sm:h-8 text-green-600"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                  />
                </svg>
              </div>
              <h3
                class="text-base sm:text-lg font-semibold text-[#111827] mb-2"
              >
                Code envoyé !
              </h3>
              <p class="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
                Vérifiez votre boîte de réception. Nous avons envoyé un code de
                réinitialisation à <strong>{{ resetEmail }}</strong>
              </p>
              <p class="text-[11px] sm:text-xs text-gray-500 mb-4">
                Le code expirera dans 15 minutes.
              </p>
              <UiButton
                variant="primary"
                size="base"
                class="w-full"
                @click="
                  navigateTo(
                    `/recovery?flow=${recoveryFlow?.id}&email=${encodeURIComponent(resetEmail)}`,
                  )
                "
                >Saisir le code</UiButton
              >
              <UiButton
                variant="secondary"
                size="base"
                class="w-full mt-2"
                @click="
                  activeTab = 'signin';
                  resetEmailSent = false;
                  resetEmail = '';
                  resetError = '';
                "
                >Retour à la connexion</UiButton
              >
            </div>
          </form>

          <!-- Divider and Google at end (only for signin/signup) -->
          <div v-if="activeTab !== 'reset'" class="pt-1">
            <div class="flex items-center gap-3 my-1">
              <div class="h-px bg-gray-200 flex-1"></div>
              <span class="text-[11px] sm:text-xs text-gray-500">ou</span>
              <div class="h-px bg-gray-200 flex-1"></div>
            </div>
            <div class="mt-2 pb-safe">
              <UiButton
                type="button"
                variant="secondary"
                size="base"
                class="w-full"
                :loading="loading"
                :disabled="loading"
                @click="onGoogleOAuth"
              >
                <div class="flex items-center justify-center gap-3">
                  <svg class="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  <span>
                    {{ activeTab === "signin" ? "Se connecter" : "S'inscrire" }}
                    avec Google
                  </span>
                </div>
              </UiButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import UiButton from "~/components/ui/base/Button.vue";
import { XMarkIcon } from "@heroicons/vue/24/outline";
import UiTextInput from "~/components/ui/form/TextInput.vue";
import { useFormValidation } from "~/composables/utils/useFormValidation";
import { useSnackbar } from "~/composables/ui/useSnackbar";
import type { LoginFlow, RegistrationFlow, RecoveryFlow } from "@ory/client";
import { logger } from "~/utils/logger";
import { useBookingStore } from "~/stores/booking";

const props = defineProps<{
  open: boolean;
  defaultTab?: "signin" | "signup";
}>();
const emit = defineEmits<{
  (e: "close"): void;
  (e: "authenticated"): void;
}>();
const auth = useAuthStore();
const kratos = useKratos();
const snackbar = useSnackbar();

const activeTab = ref<"signin" | "signup" | "reset">(
  props.defaultTab || "signin",
);
const loading = ref(false);
const resetEmailSent = ref(false);

const signin = reactive({ email: "", password: "" });
const signup = reactive({
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  confirmPassword: "",
});
const resetEmail = ref("");

// Kratos flows
const loginFlow = ref<LoginFlow | null>(null);
const registrationFlow = ref<RegistrationFlow | null>(null);
const recoveryFlow = ref<RecoveryFlow | null>(null);

// Validation state
const signInErrors = reactive<{ email?: string; password?: string }>({});
const signUpErrors = reactive<{
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
}>({});
const resetError = ref<string>("");
const signInError = ref<string>("");
const isSignUpValidating = ref(false);

const { validateEmail, validateRequired } = useFormValidation();

// Initialize Kratos flows when modal opens
watch(
  () => props.open,
  async (isOpen) => {
    if (isOpen) {
      if (activeTab.value === "signin") {
        await initLogin();
      } else if (activeTab.value === "signup") {
        await initRegistration();
      }
    }
  },
);

// Initialize flows when tab changes
watch(activeTab, async (tab) => {
  if (tab === "signin" && !loginFlow.value) {
    await initLogin();
  } else if (tab === "signup" && !registrationFlow.value) {
    await initRegistration();
  } else if (tab === "reset" && !recoveryFlow.value) {
    await initRecovery();
  }
});

async function initLogin() {
  try {
    // Check if user already has a session
    const session = await kratos.getSession();
    if (session) {
      // User has a session - check if email is verified
      try {
        await auth.checkKratosSession(true); // Throw on unverified
        // Session is valid and verified - close modal and go to dashboard
        emit("close");
        await navigateTo("/dashboard");
        return;
      } catch (verificationError: any) {
        if (verificationError?.message === "EMAIL_VERIFICATION_REQUIRED") {
          // User has session but email not verified - logout first then show login
          await kratos.logout();
        }
      }
    }

    loginFlow.value = await kratos.initLoginFlow();
  } catch (error: any) {
    // If we get 400, it might mean user already has a session
    // Try to logout and retry
    if (error?.response?.status === 400 || error?.status === 400) {
      try {
        await kratos.logout();
        loginFlow.value = await kratos.initLoginFlow();
      } catch (retryError) {
        logger.error(
          "Failed to initialize login flow after logout:",
          retryError,
        );
      }
    } else {
      logger.error("Failed to initialize login flow:", error);
    }
  }
}

async function initRecovery() {
  try {
    recoveryFlow.value = await kratos.initRecoveryFlow();
  } catch (error) {
    logger.error("Failed to initialize recovery flow:", error);
  }
}

async function initRegistration() {
  try {
    // Check if user already has a session
    const session = await kratos.getSession();
    if (session) {
      // User has a session - check if email is verified
      try {
        await auth.checkKratosSession(true); // Throw on unverified
        // Session is valid and verified - close modal and go to dashboard
        emit("close");
        await navigateTo("/dashboard");
        return;
      } catch (verificationError: any) {
        if (verificationError?.message === "EMAIL_VERIFICATION_REQUIRED") {
          // User has session but email not verified - logout first then allow registration
          await kratos.logout();
        }
      }
    }

    registrationFlow.value = await kratos.initRegistrationFlow();
  } catch (error: any) {
    // If we get 400, it might mean user already has a session
    // Try to logout and retry
    if (error?.response?.status === 400 || error?.status === 400) {
      try {
        await kratos.logout();
        registrationFlow.value = await kratos.initRegistrationFlow();
      } catch (retryError) {
        logger.error(
          "Failed to initialize registration flow after logout:",
          retryError,
        );
      }
    } else {
      logger.error("Failed to initialize registration flow:", error);
    }
  }
}

function validateSignIn(): boolean {
  signInErrors.email = validateEmail(signin.email) || undefined;
  // Password required + minimum length 6 to match backend validation
  let pwErr = validateRequired(signin.password, "Mot de passe");
  if (!pwErr && signin.password.length < 6) pwErr = "Au moins 6 caractères";
  signInErrors.password = pwErr || undefined;
  return !signInErrors.email && !signInErrors.password;
}

function validateSignUp(): boolean {
  isSignUpValidating.value = true;
  signUpErrors.firstName =
    validateRequired(signup.firstName, "Prénom") || undefined;
  signUpErrors.lastName = validateRequired(signup.lastName, "Nom") || undefined;
  signUpErrors.email = validateEmail(signup.email) || undefined;
  // Password: required + min 8 chars
  let pwErr = validateRequired(signup.password, "Mot de passe");
  if (!pwErr && signup.password.length < 8) pwErr = "Au moins 8 caractères";
  signUpErrors.password = pwErr || undefined;
  // Confirm: required + match
  let cpwErr = validateRequired(signup.confirmPassword, "Confirmation");
  if (!cpwErr && signup.password !== signup.confirmPassword) {
    cpwErr = "Les mots de passe ne correspondent pas";
  }
  signUpErrors.confirmPassword = cpwErr || undefined;
  const ok =
    !signUpErrors.firstName &&
    !signUpErrors.lastName &&
    !signUpErrors.email &&
    !signUpErrors.password &&
    !signUpErrors.confirmPassword;
  return ok;
}

watch(
  () => props.defaultTab,
  (t) => {
    if (t) activeTab.value = t;
  },
);

// Clear login error when user types
watch(
  () => [signin.email, signin.password],
  () => {
    signInError.value = "";
    signInErrors.email = undefined;
    signInErrors.password = undefined;
  },
);

const confirmError = computed(() =>
  signup.confirmPassword && signup.password !== signup.confirmPassword
    ? "Les mots de passe ne correspondent pas"
    : "",
);

const strengthScore = computed(() => {
  const pw = signup.password || "";
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (pw.length >= 12) score++;
  return Math.min(score, 6);
});
const strengthPercent = computed(() =>
  Math.round((strengthScore.value / 6) * 100),
);
const strengthLabel = computed(() => {
  const s = strengthScore.value;
  if (s <= 2) return "Faible";
  if (s <= 4) return "Moyen";
  return "Fort";
});
const strengthBarClass = computed(() => {
  const s = strengthScore.value;
  if (s <= 2) return "bg-red-500";
  if (s <= 4) return "bg-yellow-500";
  return "bg-green-500";
});
const strengthTextClass = computed(() => {
  const s = strengthScore.value;
  if (s <= 2) return "text-red-600";
  if (s <= 4) return "text-yellow-600";
  return "text-green-600";
});

async function onSignIn() {
  if (!validateSignIn()) return;
  if (!loginFlow.value) {
    await initLogin();
    if (!loginFlow.value) return;
  }

  signInError.value = "";
  loading.value = true;

  try {
    // Get CSRF token from flow
    const csrfNode = loginFlow.value.ui.nodes.find(
      (node: any) => node.attributes?.name === "csrf_token",
    );
    const csrfToken = (csrfNode?.attributes as any)?.value || "";

    // Submit login with Kratos
    const result = await kratos.submitLoginFlow(loginFlow.value.id, {
      method: "password",
      password: signin.password,
      identifier: signin.email,
      csrf_token: csrfToken,
    });

    // Check if there are errors in the response
    const errors = kratos.getFlowErrors(result);
    if (errors.length > 0) {
      signInError.value = errors[0] ?? "Email ou mot de passe incorrect";
      await initLogin(); // Reinitialize flow for next attempt
      return;
    }

    // Login successful - update auth store and check verification
    try {
      await auth.checkKratosSession(true); // Throw on unverified to handle explicitly
    } catch (verificationError: any) {
      if (verificationError?.message === "EMAIL_VERIFICATION_REQUIRED") {
        // Close modal
        emit("close");
        // Redirect to verification page
        await navigateTo("/verification");
        return;
      }
      throw verificationError;
    }

    // Clear form
    signin.email = "";
    signin.password = "";

    // Close modal - user stays on current page
    emit("close");
  } catch (e: any) {
    logger.error("Login error:", e);

    // Check for flow errors
    if (e.response?.data?.ui) {
      const errors = kratos.getFlowErrors(e.response.data);
      if (errors.length > 0) {
        signInError.value = errors[0] ?? "Email ou mot de passe incorrect";
      } else {
        signInError.value = "Email ou mot de passe incorrect";
      }
    } else {
      signInError.value = "Email ou mot de passe incorrect";
    }

    // Reinitialize flow for next attempt
    await initLogin();
  } finally {
    loading.value = false;
  }
}

async function onSignUp() {
  if (!validateSignUp()) return;
  if (!registrationFlow.value) {
    await initRegistration();
    if (!registrationFlow.value) return;
  }

  loading.value = true;

  try {
    // Get CSRF token from flow
    const csrfNode = registrationFlow.value.ui.nodes.find(
      (node: any) => node.attributes?.name === "csrf_token",
    );
    const csrfToken2 = (csrfNode?.attributes as any)?.value || "";

    // Submit registration with Kratos
    const result = await kratos.submitRegistrationFlow(
      registrationFlow.value.id,
      {
        method: "password",
        password: signup.password,
        traits: {
          email: signup.email,
          firstname: signup.firstName,
          lastname: signup.lastName,
        },
        csrf_token: csrfToken2,
      },
    );

    logger.log("Registration result:", result);

    // Check if there are errors in the response
    const errors = kratos.getFlowErrors(result);
    if (errors.length > 0) {
      snackbar.show(errors[0] ?? "", { variant: "error" });
      await initRegistration(); // Reinitialize flow for next attempt
      return;
    }

    // Registration successful
    // Clear the form
    signup.firstName = "";
    signup.lastName = "";
    signup.email = "";
    signup.password = "";
    signup.confirmPassword = "";

    // Close modal
    emit("close");

    // Show success message
    snackbar.show("Compte créé avec succès !", {
      detail: "Vérifiez votre email pour confirmer votre adresse.",
      variant: "success",
    });

    // Check if Kratos provided a redirect (via show_verification_ui hook)
    if (result.continue_with) {
      logger.log("Kratos continue_with actions:", result.continue_with);
      // Look for a redirect action
      const redirectAction = result.continue_with.find(
        (action: any) => action.action === "show_verification_ui",
      );
      if ((redirectAction as any)?.flow) {
        // Mark that user is coming from registration (use localStorage to share with popup)
        localStorage.setItem("from_registration", "true");
        // Redirect to verification page with the flow ID
        await navigateTo(
          `/verification?flow=${(redirectAction as any).flow.id}`,
        );
        return;
      }
    }

    // Fallback: redirect to verification page without flow ID
    // The verification page will create a new flow
    // Mark that user is coming from registration (use localStorage to share with popup)
    localStorage.setItem("from_registration", "true");
    await navigateTo("/verification");
  } catch (e: any) {
    logger.error("Registration error:", e);

    // Check for flow errors
    if (e.response?.data?.ui) {
      const errors = kratos.getFlowErrors(e.response.data);
      if (errors.length > 0) {
        snackbar.show(errors[0] ?? "" ?? "", { variant: "error" });
      } else {
        snackbar.show("Échec de la création du compte", { variant: "error" });
      }
    } else {
      const msg =
        e?.data?.message || e?.message || "Échec de la création du compte";
      snackbar.show(msg, { variant: "error" });
    }

    // Reinitialize flow for next attempt
    await initRegistration();
  } finally {
    loading.value = false;
    isSignUpValidating.value = false;
  }
}

async function onGoogleOAuth() {
  try {
    // Get the appropriate flow based on active tab
    const flow =
      activeTab.value === "signin" ? loginFlow.value : registrationFlow.value;

    if (!flow) {
      snackbar.show("Erreur d'initialisation", { variant: "error" });
      return;
    }

    // Get CSRF token from flow
    const csrfNode = flow.ui.nodes.find(
      (node: any) => node.attributes?.name === "csrf_token",
    );
    const csrfToken3 = (csrfNode?.attributes as any)?.value || "";

    if (!csrfToken3) {
      snackbar.show("Token de sécurité manquant", { variant: "error" });
      return;
    }

    loading.value = true;

    // Detect Android/mobile devices.
    // On Android Chrome, COOP headers from Google sever window.opener and isolate
    // the popup's sessionStorage, breaking the popup-based OAuth flow.
    // Use a full-page redirect instead, which is more compatible on mobile.
    const isAndroid = /Android/i.test(navigator.userAgent);

    const flowType = activeTab.value === "signin" ? "login" : "registration";
    const kratosUrl = useRuntimeConfig().public.kratosUrl;
    const endpoint =
      flowType === "login"
        ? `/self-service/login?flow=${flow.id}`
        : `/self-service/registration?flow=${flow.id}`;

    // Build form data (shared between popup and redirect approaches)
    const formData = new URLSearchParams();
    formData.append("csrf_token", csrfToken3);
    formData.append("provider", "google");
    formData.append("upstream_parameters.prompt", "select_account");

    // Add return_to parameter to redirect back to auth-success page after OAuth
    const returnUrl = `${window.location.origin}/auth-success`;
    formData.append("return_to", returnUrl);

    if (isAndroid) {
      // REDIRECT APPROACH for Android: full-page navigation avoids COOP/sessionStorage issues
      logger.log("📱 Android detected — using redirect OAuth flow");

      // Store the current page URL so auth-success can redirect back
      const preOAuthPath = window.location.pathname + window.location.search;
      sessionStorage.setItem("pre_oauth_url", preOAuthPath);

      const form = document.createElement("form");
      form.method = "POST";
      form.action = `${kratosUrl}${endpoint}`;
      form.target = "_self"; // Full-page navigation
      form.style.display = "none";

      for (const [key, value] of formData.entries()) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = value;
        form.appendChild(input);
      }

      document.body.appendChild(form);
      form.submit();
      // Page navigates away — no further cleanup needed
      return;
    }

    // POPUP APPROACH for desktop
    // Use localStorage event to detect auth success (avoids COOP issues)
    try {
      // Open popup FIRST (synchronously to avoid blockers)
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        "about:blank",
        "oauth_popup",
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`,
      );

      if (!popup) {
        throw new Error("Popup was blocked by browser");
      }

      // CRITICAL: Set a flag in popup's sessionStorage so it knows it's a popup
      // This persists across OAuth redirects when window.opener is lost
      try {
        popup.sessionStorage.setItem("is_oauth_popup", "true");
        logger.log("✅ Set is_oauth_popup flag in popup sessionStorage");
      } catch (e) {
        logger.error("❌ Failed to set popup flag:", e);
      }

      // Show loading in popup
      popup.document.write(`
        <html>
          <head><title>Connexion...</title></head>
          <body style="font-family: sans-serif; display: flex; align-items: center; justify-content: center; height: 100vh; margin: 0;">
            <div style="text-align: center;">
              <div style="border: 3px solid #f3f3f3; border-top: 3px solid #D0A444; border-radius: 50%; width: 40px; height: 40px; animation: spin 1s linear infinite; margin: 0 auto 20px;"></div>
              <p>Connexion en cours...</p>
            </div>
            <style>@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); }}</style>
          </body>
        </html>
      `);

      logger.log("🪟 Opened OAuth popup");

      // Create form and submit to popup
      const form = document.createElement("form");
      form.method = "POST";
      form.action = `${kratosUrl}${endpoint}`;
      form.target = "oauth_popup";
      form.style.display = "none";

      for (const [key, value] of formData.entries()) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = value;
        form.appendChild(input);
      }

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);

      // Track when popup was opened
      const popupOpenTime = Date.now();
      let popupWasClosed = false;
      let checkingAuth = false;

      // Poll for popup closure and authentication
      const authCheckInterval = setInterval(async () => {
        // Skip if already checking
        if (checkingAuth) return;

        // Check if popup is closed
        let isPopupClosed = false;
        try {
          isPopupClosed = popup.closed;
        } catch (e) {
          // COOP error - can't reliably check, so check if enough time passed
          const timeSinceOpen = Date.now() - popupOpenTime;
          if (timeSinceOpen > 3000) {
            // After 3 seconds, start checking for auth regardless
            isPopupClosed = true;
          }
        }

        if (isPopupClosed && !popupWasClosed) {
          popupWasClosed = true;
          logger.log(
            "🚪 Popup closed detected, checking for authentication...",
          );

          // Wait 1 second for cookies to sync across windows
          await new Promise((resolve) => setTimeout(resolve, 1000));

          checkingAuth = true;

          try {
            const authStore = useAuthStore();

            // Force a fresh session check
            authStore.sessionRestored = false;
            await authStore.restoreSession();

            if (authStore.isAuthenticated) {
              logger.log("✅ Authentication successful!");
              clearInterval(authCheckInterval);
              loading.value = false;

              // Check if verification is required
              if (authStore.verificationRequired) {
                logger.log("⚠️ Email verification required");
                localStorage.setItem("from_registration", "true");
              } else {
                snackbar.show("Connexion réussie!", { variant: "success" });
                emit("close");
                emit("authenticated");
              }
            } else {
              logger.log(
                "❌ No authentication detected, user likely cancelled",
              );
              clearInterval(authCheckInterval);
              loading.value = false;
            }
          } catch (error) {
            logger.error("Error checking auth after popup close:", error);
            clearInterval(authCheckInterval);
            loading.value = false;
          }

          checkingAuth = false;
        }
      }, 500);

      // Safety timeout - 2 minutes
      setTimeout(
        () => {
          clearInterval(authCheckInterval);
          try {
            popup.close();
          } catch (e) {}
          loading.value = false;
          logger.log("⏱️ OAuth timeout - stopped polling");
        },
        2 * 60 * 1000,
      );
    } catch (error: any) {
      loading.value = false;
      logger.error("Failed to open OAuth popup:", error);

      if (error.message?.includes("blocked")) {
        snackbar.show("Veuillez autoriser les popups pour ce site", {
          variant: "info",
        });
      } else {
        snackbar.show("Erreur lors de la connexion", { variant: "error" });
      }
    }
  } catch (error: any) {
    loading.value = false;

    // 422 or 303 responses mean Kratos is redirecting - this is EXPECTED
    if (error.response?.status === 422 || error.response?.status === 303) {
      logger.log("OAuth redirect initiated (this is expected)");
      return; // Browser will handle the redirect
    }

    logger.error("Google OAuth error:", error);
    snackbar.show("Erreur lors de la connexion avec Google", {
      variant: "error",
    });
  }
}

async function onResetPassword() {
  // Validate email
  resetError.value = validateEmail(resetEmail.value) || "";
  if (resetError.value) return;

  if (!recoveryFlow.value) {
    await initRecovery();
    if (!recoveryFlow.value) return;
  }

  loading.value = true;
  resetError.value = "";

  try {
    // Extract CSRF token from recovery flow
    const csrfToken = (
      recoveryFlow.value.ui.nodes.find(
        (node: any) => node.attributes.name === "csrf_token",
      )?.attributes as any
    )?.value;

    logger.log("Submitting recovery flow for email:", resetEmail.value);

    // Submit recovery flow to Kratos (send email with code)
    const result = await kratos.submitRecoveryFlow(
      recoveryFlow.value.id,
      resetEmail.value,
      csrfToken,
    );

    logger.log("Recovery flow result:", result);

    // Update flow with result
    recoveryFlow.value = result;

    // Check if email was sent successfully
    if (result.state === "sent_email") {
      resetEmailSent.value = true;
    } else {
      throw new Error("Email not sent - flow state: " + result.state);
    }
  } catch (e: any) {
    logger.error("Recovery flow submission error:", e);
    const errors = kratos.getFlowErrors(e.response?.data || {});
    const errorMsg = errors[0] ?? "Erreur lors de l'envoi de l'email";
    resetError.value = errorMsg;
    // Reinitialize flow on error
    await initRecovery();
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Mobile slide-up animation for modal content only */
@media (max-width: 640px) {
  .fade-enter-active > :last-child {
    transition: transform 0.3s ease;
  }

  .fade-leave-active > :last-child {
    transition: transform 0.3s ease;
  }

  .fade-enter-from > :last-child {
    transform: translateY(100%);
  }

  .fade-leave-to > :last-child {
    transform: translateY(100%);
  }

  .fade-enter-to > :last-child,
  .fade-leave-from > :last-child {
    transform: translateY(0);
  }
}
</style>
